import React, { useState, useEffect, useRef } from "react";
import { useField, useFormikContext } from "formik";
import { Button } from "@mui/material";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Cross from "../../assets/Home/Cross.svg";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Search from "../../assets/Home/Search.svg";
import UploadImage from "assets/images/UploadImage.png";
import UploadCloud from "assets/icons/UploadCloud.svg";
import Close from "../../assets/icons/Close.svg";
import InputMask from "react-input-mask";

const FormField = ({
  label,
  name,
  type = "text",
  placeholder,
  options = [],
  className = "",
  error,
  touched,
  startIcon,
  ...props
}) => {
  const [field, meta, helpers] = useField(name);
  const { validateField } = useFormikContext?.() || {};
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Use passed error and touched props, fallback to meta from useField
  const fieldError = error || meta.error;
  const fieldTouched = touched || meta.touched;
  const hasError = fieldTouched && fieldError;

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm(""); // Clear search when closing dropdown
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Cleanup object URL for file previews
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const renderField = () => {
    switch (type) {
      case "multiselect":
        const selectedValues = field.value || [];

        const handleOptionToggle = (optionValue) => {
          const newValues = selectedValues.includes(optionValue)
            ? selectedValues.filter((value) => value !== optionValue)
            : [...selectedValues, optionValue];
          helpers.setValue(newValues);
        };

        const removeChip = (chipValue) => {
          const newValues = selectedValues.filter(
            (value) => value !== chipValue
          );
          helpers.setValue(newValues);
        };

        const getSelectedLabels = () => {
          return selectedValues.map(
            (value) =>
              options.find((option) => option.value === value)?.label || value
          );
        };

        return (
          <div className="relative" ref={dropdownRef}>
            <div
              className={`w-full bg-[#F8F8F8]  sm2:min-w-[270px] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 cursor-pointer ${
                hasError ? "border-red-500 border" : ""
              } ${className}`}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <div className="flex items-center justify-between">
                <span
                  className={
                    selectedValues.length === 0
                      ? "text-gray-400"
                      : "text-gray-700"
                  }
                >
                  {selectedValues.length === 0
                    ? placeholder
                    : `${selectedValues.length} selected`}
                </span>
                <svg
                  className={`w-4 h-4 transition-transform ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            {/* Selected chips */}
            {selectedValues.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {getSelectedLabels().map((label, index) => (
                  <Chip
                    sx={{
                      backgroundColor: "#F8F8F8",
                      fontWeight: 500,
                      fontSize: "12px",
                      color: "#595959",
                      padding: "4px 8px",
                      lineHeight: "18px",
                      "& .MuiChip-deleteIcon": {
                        color: "#595959",
                        fontSize: "16px",
                        "&:hover": {
                          color: "#333333",
                        },
                      },
                    }}
                    key={selectedValues[index]}
                    label={label}
                    onDelete={() => removeChip(selectedValues[index])}
                    deleteIcon={
                      <img
                        src={Cross}
                        alt="Remove"
                        style={{ width: "8px", height: "8px" }}
                      />
                    }
                    variant="outlined"
                    className="bg-[#F8F8F8]"
                  />
                ))}
              </div>
            )}

            {/* Dropdown options */}
            {isDropdownOpen && (
              <div className="absolute z-10 w-full mt-3 bg-white border border-[#F8F8F8] rounded-[12px] shadow-lg max-h-60 overflow-auto">
                {/* Search input */}
                <div className="sticky top-0 bg-white border-b border-[#F8F8F8]  pr-3">
                  <div className="relative">
                    <img
                      src={Search}
                      alt="Search"
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                    />
                    <input
                      type="text"
                      placeholder="Search for categories"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 h-[34px]  text-sm border-none outline-none bg-transparent"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>

                {/* Options list */}
                {filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`px-3 py-2 text-[12px] leading-[18px] text-[#333333] cursor-pointer hover:bg-gray-100 ${
                      selectedValues.includes(option.value) ? "bg-green-50" : ""
                    }`}
                    onClick={() => handleOptionToggle(option.value)}
                  >
                    <div className="flex items-center text-[12px] font-[500]">
                      <input
                        type="checkbox"
                        checked={selectedValues.includes(option.value)}
                        onChange={() => {}}
                        className="h-4 w-4  text-green-500 mr-2 accent-[#3CAB5C] border-[#DADADA]"
                      />
                      <span>{option.label}</span>
                    </div>
                  </div>
                ))}

                {/* No results message */}
                {filteredOptions.length === 0 && searchTerm && (
                  <div className="px-3 py-2 text-gray-500 text-sm">
                    No categories found
                  </div>
                )}
              </div>
            )}

            {hasError && (
              <div className="mt-1 text-sm text-red-600">{fieldError}</div>
            )}
          </div>
        );

      case "select":
        return (
          <>
            <select
              {...field}
              className={`w-full bg-[#F8F8F8]  font-neue-haas  sm2:min-w-[270px]  rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                hasError ? "border-red-500 border" : ""
              } ${
                props.disabled ? "opacity-50 cursor-not-allowed" : ""
              } ${className}`}
              disabled={props.disabled}
              {...props}
            >
              <option value="">{placeholder || `Select ${label}`}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {hasError && (
              <div className="mt-1 text-sm text-red-600">{fieldError}</div>
            )}
          </>
        );

      case "radio":
        return (
          <>
            <div className="flex md:flex-row flex-col md:gap-4 gap-2">
              {options.map((option) => (
                <label key={option.value} className="flex items-center">
                  <input
                    {...field}
                    type="radio"
                    value={option.value}
                    checked={field.value === option.value}
                    onChange={() => helpers.setValue(option.value)}
                    className="h-4 w-4 text-green-500 font-neue-haas mr-2 accent-green-500"
                    {...props}
                  />
                  <span className="text-[#333333] font-neue-haas">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
            {hasError && (
              <div className="mt-1 text-sm text-red-600">{fieldError}</div>
            )}
          </>
        );

      case "textarea":
        return (
          <>
            <textarea
              {...field}
              placeholder={placeholder}
              className={`w-full bg-[#F8F8F8] rounded-[8px] font-neue-haas px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none ${
                hasError ? "border-red-500 border" : ""
              } ${className}`}
              rows={4}
              {...props}
            />
            {hasError && (
              <div className="mt-1 text-sm  text-red-600">{fieldError}</div>
            )}
          </>
        );

      case "checkbox":
        const checkboxValues = field.value || [];

        const handleCheckboxChange = (optionValue) => {
          const newValues = checkboxValues.includes(optionValue)
            ? checkboxValues.filter((value) => value !== optionValue)
            : [...checkboxValues, optionValue];
          helpers.setValue(newValues);
        };

        return (
          <>
            <div className="flex flex-row font-neue-haas gap-4">
              {options.map((option) => (
                <label key={option.value} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={checkboxValues.includes(option.value)}
                    onChange={() => handleCheckboxChange(option.value)}
                    className="h-4 w-4 text-green-500 mr-2 font-neue-haas accent-green-500 border-[#DADADA]"
                    {...props}
                  />
                  <span className="text-[#333333] font-neue-haas">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
            {hasError && (
              <div className="mt-1 text-sm text-red-600">{fieldError}</div>
            )}
          </>
        );

      case "toggle":
        return (
          <>
            <div className="flex font-neue-haas items-center gap-4">
              <label className="flex items-center font-neue-haas cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={field.value === true}
                    onChange={() => helpers.setValue(!field.value)}
                    className="sr-only"
                    {...props}
                  />
                  <div
                    className={`w-12 h-6 rounded-full font-neue-haas transition-colors duration-200 flex items-center ${
                      field.value === true ? "bg-green-500" : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 bg-white font-neue-haas rounded-full transition-transform duration-200 transform ${
                        field.value === true ? "translate-x-6" : "translate-x-1"
                      }`}
                    ></div>
                  </div>
                </div>
              </label>
            </div>
            {hasError && (
              <div className="mt-1 text-sm text-red-600">{fieldError}</div>
            )}
          </>
        );

      case "file":
        return (
          <>
            <div className="border border-dashed font-neue-haas border-gray-300 rounded-[16px] p-[25px]">
              <div className="flex flex-col items-center justify-center font-neue-haas">
                <img
                  src={UploadImage}
                  alt="Upload Image"
                  className="w-[42px] h-[42px] mb-4"
                />
                <div className="flex flex-col items-center justify-center gap-2">
                  <h4 className="text-center text-[#595959] font-neue-haas  text-[10px] leading-5 tracking-[0%] w-full">
                    Upload files in PDF, JPG or PNG format. Maximum file size:
                    5MB per file.
                  </h4>

                  <label
                    className={`cursor-pointer bg-[#F3F3F3] text-[10px] font-medium text-[#242424] hover:bg-gray-200 py-2 px-4 rounded-[6px] gap-1 flex items-center justify-center ${
                      hasError && !isFileLoading ? "border-red-500 border" : ""
                    }`}
                  >
                    <img src={UploadCloud} alt="Upload Cloud" />
                    Upload File
                    <input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,.jpg,.jpeg,.png,.pdf"
                      onChange={(event) => {
                        const file = event.currentTarget.files && event.currentTarget.files[0];
                        if (!file) {
                          helpers.setValue(null);
                          setPreviewUrl(null);
                          setIsFileLoading(false);
                          validateField && validateField(name);
                          return;
                        }
                        helpers.setValue(file);
                        helpers.setTouched(true, false);
                        if (helpers.setError) helpers.setError(undefined);
                        if (file.type && file.type.startsWith("image/")) {
                          const url = URL.createObjectURL(file);
                          setPreviewUrl((prev) => {
                            if (prev) URL.revokeObjectURL(prev);
                            return url;
                          });
                          setIsFileLoading(true);
                        } else {
                          setPreviewUrl(null);
                          setIsFileLoading(false);
                          validateField && validateField(name);
                        }
                      }}
                      className="hidden"
                      {...props}
                    />
                  </label>

                  {field.value && (
                    <div className="mt-3 w-full">
                      <div className={`${hasError ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"} border rounded-lg p-3`}>
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0">
                            {field.value && field.value.type && field.value.type.startsWith("image/") ? (
                              <img
                                src={previewUrl || (field.value && URL.createObjectURL(field.value))}
                                alt="Preview"
                                className="w-12 h-12 object-cover rounded border"
                                onLoad={() => {
                                  setIsFileLoading(false);
                                  validateField && validateField(name);
                                }}
                                onError={() => {
                                  setIsFileLoading(false);
                                  validateField && validateField(name);
                                }}
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-100 rounded border flex items-center justify-center">
                                <svg
                                  className="w-6 h-6 text-gray-500"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {field.value?.name || "Unknown File"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {field.value?.size ? (field.value.size / 1024 / 1024).toFixed(2) : "0.00"} MB
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              helpers.setValue(null);
                              helpers.setTouched(false, false);
                              const fileInput =
                                document.querySelector('input[type="file"]');
                              if (fileInput) fileInput.value = "";
                              if (previewUrl) {
                                URL.revokeObjectURL(previewUrl);
                                setPreviewUrl(null);
                              }
                              setIsFileLoading(false);
                              validateField && validateField(name);
                            }}
                            className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <img src={Close} alt="Close" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {isFileLoading && (
                    <div className="mt-3 text-xs text-[#595959]">Loading preview…</div>
                  )}
                  {hasError && !isFileLoading && (
                    <div className="mt-3 text-sm text-red-500">{fieldError}</div>
                  )}
                </div>
              </div>
            </div>
          </>
        );

      default:
        return (
          <>
            <div className="relative">
              {startIcon && (
                <span className="absolute left-3 top-1/2 font-neued-haas transform -translate-y-1/2 w-4 h-4 text-gray-400">
                  <img src={startIcon} alt={label} />
                </span>
              )}
              {props.mask ? (
                <InputMask
                  mask={props.mask}
                  maskChar={props.maskChar}
                  value={field.value || ""}
                  onChange={(e) => helpers.setValue(e.target.value)}
                  disabled={props.disabled}
                >
                  {(inputProps) => (
                    <input
                      {...inputProps}
                      type={type}
                      placeholder={placeholder}
                      disabled={props.disabled}
                      className={`w-full bg-[#F8F8F8]  font-neue-haas sm2:min-w-[270px] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                        startIcon ? "pl-8" : "pl-3"
                      } ${hasError ? "border-red-500 border" : ""} ${
                        props.disabled ? "opacity-50 cursor-not-allowed" : ""
                      } ${className}`}
                    />
                  )}
                </InputMask>
              ) : (
                <input
                  {...field}
                  type={type}
                  placeholder={placeholder}
                  className={`w-full bg-[#F8F8F8]  font-neue-haas sm2:min-w-[270px] rounded-[8px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    startIcon ? "pl-8" : "pl-3"
                  } ${hasError ? "border-red-500 border" : ""} ${className}`}
                  {...props}
                />
              )}
            </div>
            {hasError && (
              <div className="mt-1 text-sm text-red-600">{fieldError}</div>
            )}
          </>
        );
    }
  };

  return (
    <div
      className={`mb-2 ${
        type === "toggle"
          ? "flex flex-row gap-2 font-neue-haas items-center"
          : ""
      }`}
    >
      <label
        htmlFor={name}
        style={{ letterSpacing: "0.035em" }}
        className={`block text-[12px] leading-[18px] font-neue-haas font-semibold text-[#333333] ${
          type === "toggle" ? "mb-0" : "mb-1"
        }`}
      >
        {label}
      </label>
      {renderField()}
    </div>
  );
};

export default FormField;
